import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import './Home.css';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blogsLoading, setBlogsLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedProducts();
    fetchFeaturedBlogs();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const productsRef = collection(db, 'products');
      const q = query(productsRef, where('isFeatured', '==', true));
      const snapshot = await getDocs(q);
      const products = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort products by featuredSequence (lower numbers first)
      const sortedProducts = products.sort((a, b) => {
        const sequenceA = a.featuredSequence || 0;
        const sequenceB = b.featuredSequence || 0;
        return sequenceA - sequenceB;
      });
      
      console.log('Fetched featured products from Firestore:', sortedProducts.length);
      setFeaturedProducts(sortedProducts);
    } catch (error) {
      console.error('Error fetching featured products:', error);
      // Return empty array instead of mock data
      console.warn('Returning empty featured products array due to error');
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedBlogs = async () => {
    try {
      const blogsRef = collection(db, 'blogs');
      const q = query(blogsRef, where('isFeatured', '==', true));
      const snapshot = await getDocs(q);
      const blogs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Fetched featured blogs from Firestore:', blogs.length);
      setFeaturedBlogs(blogs);
    } catch (error) {
      console.error('Error fetching featured blogs:', error);
      // Return empty array instead of mock data
      console.warn('Returning empty featured blogs array due to error');
      setFeaturedBlogs([]);
    } finally {
      setBlogsLoading(false);
    }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                Welcome to
              </h1>
              <h1 className="hero-brand">
                <span className="highlight">DHRTI MAA</span>
              </h1>
              <p className="hero-subtitle">Inspired By Nature</p>
              <p className="hero-description">
                Your trusted partner for quality gardening needs. We provide natural solutions for your gardens & farms. We are commited to "Redefining gardening with nature-inspired care and eco-friendly innovation."
              </p>
              <div className="hero-actions">
                <Link to="/products" className="btn btn-primary">
                  Explore Products
                </Link>
                <Link to="/about" className="btn btn-primary">
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="featured-products">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Featured Products</h2>
            <p className="section-subtitle">Discover our most popular gardening solutions</p>
          </div>
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading featured products...</p>
            </div>
          ) : (
            <>
              <div className={`products-grid ${featuredProducts.length <= 2 ? 'few-items' : ''}`}>
                {featuredProducts.length > 0 ? (
                  featuredProducts.map(product => (
                    <div key={product.id} className="product-card">
                      <div className="product-image">
                        <img src={product.image || '/logo.png'} alt={product.name} />
                      </div>
                      <div className="product-info">
                        <span className="product-category">{product.category}</span>
                        <h3 className="product-name">{product.name}</h3>
                        <div className="product-footer">
                          <Link to={`/products/${product.slug || product.id}`} className="btn btn-primary btn-sm">
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-products">
                    <h3>No featured products yet</h3>
                    <p>Add products and mark them as featured to see them here.</p>
                  </div>
                )}
              </div>
              
              {featuredProducts.length > 0 && (
                <div className="section-footer">
                  <Link to="/products" className="btn btn-secondary">
                    View All Products
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Latest Blogs Section */}
      <section className="latest-blogs">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Latest from Our Blog</h2>
            <p className="section-subtitle">Expert tips and gardening insights</p>
          </div>
          
          {blogsLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading blog posts...</p>
            </div>
          ) : (
            <>
              <div className={`blogs-grid ${featuredBlogs.length <= 2 ? 'few-items' : ''}`}>
                {featuredBlogs.length > 0 ? (
                  featuredBlogs.map(blog => (
                    <article key={blog.id} className="blog-card">
                      <div className="blog-image">
                        <img src={blog.image || '/logo.png'} alt={blog.title} />
                      </div>
                      <div className="blog-content">
                        <div className="blog-meta">
                          <span className="blog-date">{blog.publishedAt}</span>
                          <span className="blog-read-time">{blog.readTime}</span>
                        </div>
                        <h3 className="blog-title">{blog.title}</h3>
                        <p className="blog-excerpt">{blog.excerpt}</p>
                        <Link to={`/blog/${blog.slug || blog.id}`} className="blog-link">
                          Read More →
                        </Link>
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="no-blogs">
                    <h3>No featured blog posts yet</h3>
                    <p>Add blog posts and mark them as featured to see them here.</p>
                  </div>
                )}
              </div>
              
              {featuredBlogs.length > 0 && (
                <div className="section-footer">
                  <Link to="/blog" className="btn btn-secondary">
                    View All Articles
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Contact Banner */}
      <section className="contact-banner">
        <div className="container">
          <div className="banner-content">
            <div className="banner-text">
              <h2 className="banner-title">Need Help with Your Garden?</h2>
              <p className="banner-description">
                Our experts are here to help you choose the right products and provide gardening advice.
              </p>
            </div>
            <div className="banner-actions">
              <Link to="/contact" className="btn btn-primary">
                Contact Us
              </Link>
              <a href="tel:+918890060211" className="btn btn-primary">
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
